import * as Api from '@ViewModels';
import type { Edge, Node } from '@xyflow/react';
import type {
	BehaviorCaseStatementPayload,
	BehaviorSwitchStepPayload,
} from '../../../../queries/Automation/mutations/behaviorSwitch/types';
import { NODE_TYPES, type BaseNodeData } from '../types';
import { EDGE_TYPES } from '../types/edges';
import type { AutomationStepNodeData, BehaviorCaseNodeData, ParentCaseInfo } from '../types/stepNodes';
import { hasEstimatedHeight } from '../types/typeGuards';
import { applySwitchBranchLayout } from './layoutEngine';
import { STEP_NODE_EXPANDED_MIN_HEIGHT } from './layoutConstants';
import { getDescendantNodeIds } from './graphHelpers';
import { createBehaviorCaseNode } from './nodeFactory';

export interface BehaviorCaseLike extends Omit<BehaviorCaseStatementPayload, 'engagementType' | '_type'> {
	_type?: BehaviorCaseStatementPayload['_type'];
	caseType?: string;
	engagementType?: string | number;
	isDefault?: boolean;
	isTimeout?: boolean;
}

export interface BehaviorSwitchLike {
	id?: string;
	_type?: Api.ApiModelType | 'BehaviorSwitchAutomationStep' | string;
	sourceStepId?: string;
	waitTimeout?: string;
	cases?: BehaviorCaseLike[];
}

export type BehaviorCaseKind = 'reply' | 'click' | 'timeout';

export interface BehaviorGraphState {
	nextReply: boolean;
	nextClick: boolean;
	nextTimeoutDays: number;
}

interface PersistedBehaviorCaseAssignment {
	kind: BehaviorCaseKind;
	caseId?: string;
	caseName?: string;
	caseType?: string;
	engagementType?: string;
	childTemplateId?: string;
	isDefault: boolean;
	isTimeout: boolean;
	caseColor?: string;
}

const DEFAULT_TIMEOUT_CASE_LABEL = 'If no engagement (timeout)';

// When the first behavior branch is enabled, the source step immediately grows
// to reveal the timeout editor. React Flow's measured height can lag by a frame,
// which lets freshly spawned behavior cases overlap the parent card. Add a
// temporary height buffer so the optimistic layout reserves enough space until
// ResizeObserver reports the exact post-toggle height.
const OPTIMISTIC_BEHAVIOR_SOURCE_HEIGHT_BUFFER = 160;

function getExpandedSourceHeight(node: Node<BaseNodeData>): number | null {
	const isExpanded = (node.data as { isExpanded?: unknown } | undefined)?.isExpanded === true;
	if (!isExpanded) {
		return null;
	}

	const measuredHeight = typeof node.measured?.height === 'number' ? node.measured.height : null;
	const estimatedHeight = hasEstimatedHeight(node.data) ? node.data.estimatedHeight : null;
	return measuredHeight !== null && estimatedHeight !== null
		? Math.max(measuredHeight, estimatedHeight)
		: (measuredHeight ?? estimatedHeight ?? STEP_NODE_EXPANDED_MIN_HEIGHT);
}

function withOptimisticBehaviorSourceHeight(node: Node<BaseNodeData>, shouldPadHeight: boolean): Node<BaseNodeData> {
	if (!shouldPadHeight) {
		return node;
	}

	const currentHeight = getExpandedSourceHeight(node);
	if (currentHeight === null) {
		return node;
	}

	const nextEstimatedHeight = currentHeight + OPTIMISTIC_BEHAVIOR_SOURCE_HEIGHT_BUFFER;
	const existingEstimatedHeight = hasEstimatedHeight(node.data) ? node.data.estimatedHeight : null;

	if (existingEstimatedHeight !== null && existingEstimatedHeight >= nextEstimatedHeight) {
		return node;
	}

	return {
		...node,
		data: {
			...node.data,
			estimatedHeight: nextEstimatedHeight,
		},
	};
}

function caseTypeOf(candidate: BehaviorCaseLike): string {
	return ((candidate.caseType || candidate._type || '') as string).toLowerCase();
}

function engagementTypeOf(candidate: BehaviorCaseLike): string {
	if (typeof candidate.engagementType === 'string') {
		return candidate.engagementType.toLowerCase();
	}

	if (typeof candidate.engagementType === 'number') {
		switch (candidate.engagementType) {
			case 0:
				return 'reply';
			case 1:
				return 'open';
			case 2:
				return 'anylinkclick';
			case 3:
				return 'specificlinkclick';
			default:
				return '';
		}
	}

	return '';
}

function caseNameOf(candidate: BehaviorCaseLike): string {
	return (candidate.name || '').toLowerCase();
}

export function isTimeoutBehaviorCase(candidate: BehaviorCaseLike): boolean {
	const caseType = caseTypeOf(candidate);
	return (
		candidate.isDefault === true ||
		candidate.isTimeout === true ||
		caseType.includes('timeoutcasestatement') ||
		caseType.includes('defaultcasestatement')
	);
}

export function isReplyBehaviorCase(candidate: BehaviorCaseLike): boolean {
	const caseType = caseTypeOf(candidate);
	const engagement = engagementTypeOf(candidate);
	const caseName = caseNameOf(candidate);

	if (caseType.includes('textengagementcasestatement')) {
		return true;
	}

	if (caseType.includes('emailengagementcasestatement')) {
		return engagement.includes('reply') || caseName.includes('repl');
	}

	return caseName.includes('repl') && !caseName.includes('click');
}

export function isClickBehaviorCase(candidate: BehaviorCaseLike): boolean {
	const caseType = caseTypeOf(candidate);
	const engagement = engagementTypeOf(candidate);
	const caseName = caseNameOf(candidate);

	if (caseType.includes('emailengagementcasestatement')) {
		return (
			engagement.includes('anylinkclick') ||
			engagement.includes('specificlinkclick') ||
			engagement.includes('click') ||
			caseName.includes('click')
		);
	}

	return caseName.includes('click');
}

export function getBehaviorCaseKind(candidate: BehaviorCaseLike): BehaviorCaseKind | null {
	if (isTimeoutBehaviorCase(candidate)) {
		return 'timeout';
	}
	if (isReplyBehaviorCase(candidate)) {
		return 'reply';
	}
	if (isClickBehaviorCase(candidate)) {
		return 'click';
	}
	return null;
}

function getBehaviorCaseNodeKind(candidate: Partial<BehaviorCaseNodeData>): BehaviorCaseKind | null {
	const caseLike: BehaviorCaseLike = {
		id: typeof candidate.caseId === 'string' ? candidate.caseId : undefined,
		name: typeof candidate.caseName === 'string' ? candidate.caseName : candidate.label,
		_type: (candidate.caseType || 'TimeoutCaseStatement') as unknown as BehaviorCaseStatementPayload['_type'],
		caseType: candidate.caseType,
		engagementType: candidate.engagementType,
		isDefault: candidate.isDefault,
		isTimeout: candidate.isTimeout,
	};
	return getBehaviorCaseKind(caseLike);
}

function getBehaviorParentCaseKind(parentCaseInfo: ParentCaseInfo): BehaviorCaseKind | null {
	const caseLike: BehaviorCaseLike = {
		id: parentCaseInfo.caseId,
		name: parentCaseInfo.caseName,
		isDefault: parentCaseInfo.isDefault,
		isTimeout: parentCaseInfo.isDefault,
	};
	return getBehaviorCaseKind(caseLike);
}

export function isBehaviorSwitchModel(step: { _type?: unknown }): boolean {
	return ((step._type || '') as string) === 'BehaviorSwitchAutomationStep';
}

export function buildLocalBehaviorSegmentPathKey({
	templateId,
	parentCaseInfo,
	switchNodeId,
	caseId,
	caseIndex,
}: {
	templateId?: string;
	parentCaseInfo?: ParentCaseInfo;
	switchNodeId: string;
	caseId: string;
	caseIndex: number;
}): string {
	const lineage = parentCaseInfo?.segmentPathKey || `template:${templateId || 'unknown-template'}`;
	return `${lineage}>behaviorCase:${templateId || 'unknown-template'}:${switchNodeId}:${caseId}:${caseIndex}`;
}

function createOptimisticBehaviorSwitchNode({
	switchNodeId,
	sourceNode,
	template,
	sourceStepId,
	waitTimeout,
	cases,
}: {
	switchNodeId: string;
	sourceNode: Node<BaseNodeData>;
	template?: Api.IAutomationTemplate;
	sourceStepId: string;
	waitTimeout: string;
	cases: BehaviorCaseLike[];
}): Node<BaseNodeData> {
	const stepId = switchNodeId.replace(/^step-/, '');
	const sourceData = sourceNode.data as Partial<AutomationStepNodeData>;

	return {
		id: switchNodeId,
		type: NODE_TYPES.SWITCH_STEP,
		hidden: true,
		position: sourceNode.position,
		data: {
			nodeId: switchNodeId,
			stepId,
			stepType: Api.AutomationStepType.Switch,
			label: 'Conditional Behavior',
			summary: cases.length > 0 ? `${cases.length} behavior paths` : 'No behavior paths',
			category: 'action',
			automationStep: {
				_type: 'BehaviorSwitchAutomationStep',
				id: stepId,
				name: 'Conditional Behavior',
				sourceStepId,
				waitTimeout,
				cases,
			} as unknown as Api.IAutomationStep,
			automationTemplate: template,
			isExpanded: false,
			isCompact: true,
			isValid: true,
			validationErrors: [],
			stepData: {
				name: 'Conditional Behavior',
			},
			outputHandles: ['default'],
			metadata: {
				stepType: 'BehaviorSwitchAutomationStep',
			},
			targetPosition: sourceData.targetPosition,
			sourcePosition: sourceData.sourcePosition,
		} as AutomationStepNodeData,
	};
}

export function findBehaviorSwitchForSourceStep(
	steps: Api.IAutomationStep[],
	sourceStepId: string | undefined
): BehaviorSwitchLike | null {
	if (!sourceStepId) {
		return null;
	}

	const explicitSwitch = steps.find(candidate => {
		const behaviorCandidate = candidate as BehaviorSwitchLike;
		return isBehaviorSwitchModel(behaviorCandidate) && behaviorCandidate.sourceStepId === sourceStepId;
	}) as BehaviorSwitchLike | undefined;
	if (explicitSwitch) {
		return explicitSwitch;
	}

	const sourceStepIndex = steps.findIndex(candidate => candidate.id === sourceStepId);
	if (sourceStepIndex < 0) {
		return null;
	}

	for (let i = sourceStepIndex + 1; i < steps.length; i++) {
		const candidate = steps[i] as BehaviorSwitchLike;
		if (!isBehaviorSwitchModel(candidate)) {
			break;
		}

		if (!candidate.sourceStepId || candidate.sourceStepId === sourceStepId) {
			return candidate;
		}
	}

	return null;
}

export function applyOptimisticBehaviorGraphUpdate({
	nodes,
	edges,
	step,
	template,
	parentCaseInfo,
	sourceStepId,
	nextState,
	getSourceStepName,
	createReplyCasePayload,
	createClickCasePayload,
	createTimeoutCasePayload,
}: {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
	step: Api.IAutomationStep | null;
	template?: Api.IAutomationTemplate;
	parentCaseInfo?: ParentCaseInfo;
	sourceStepId?: string;
	nextState: BehaviorGraphState;
	getSourceStepName: (
		step: Api.IAutomationStep | null,
		nodes: Node<BaseNodeData>[],
		sourceNodeId: string
	) => string | undefined;
	createReplyCasePayload: (step: Api.IAutomationStep | null) => BehaviorCaseStatementPayload;
	createClickCasePayload: () => BehaviorCaseStatementPayload;
	createTimeoutCasePayload: (name: string) => BehaviorCaseStatementPayload;
}): { nodes: Node<BaseNodeData>[]; edges: Edge[] } {
	if (!sourceStepId) {
		return { nodes, edges };
	}

	const sourceNodeId = `step-${sourceStepId}`;
	const sourceNode = nodes.find(node => node.id === sourceNodeId);
	if (!sourceNode) {
		return { nodes, edges };
	}

	const existingSwitchNode = nodes.find(node => {
		const automationStep = (node.data as { automationStep?: BehaviorSwitchLike }).automationStep;
		if (!automationStep || !isBehaviorSwitchModel(automationStep)) {
			return false;
		}
		// Primary match: sourceStepId on the step data.
		if (automationStep.sourceStepId === sourceStepId) {
			return true;
		}
		// Fallback: after server persistence, sourceStepId may be missing from the
		// expanded node data. Match by checking if branch edges from the source node
		// reference this switch node.
		return edges.some(edge => {
			if (edge.source !== sourceNodeId || edge.type !== EDGE_TYPES.BRANCH) {
				return false;
			}
			return (edge.data as { switchNodeId?: string } | undefined)?.switchNodeId === node.id;
		});
	});
	const switchNodeId = existingSwitchNode?.id || `step-temp-behavior-switch-${sourceStepId}`;
	const existingSwitchStep = (existingSwitchNode?.data as { automationStep?: BehaviorSwitchLike } | undefined)
		?.automationStep;
	const existingCases = existingSwitchStep?.cases || [];
	const existingCaseNodes = nodes.filter(node => {
		if (node.type !== NODE_TYPES.BEHAVIOR_CASE) {
			return false;
		}
		return (node.data as BehaviorCaseNodeData).parentSwitchNodeId === switchNodeId;
	});
	const existingCaseNodeByKind = new Map<BehaviorCaseKind, Node<BaseNodeData>>();
	for (const node of existingCaseNodes) {
		const kind = getBehaviorCaseNodeKind(node.data as BehaviorCaseNodeData);
		if (kind) {
			existingCaseNodeByKind.set(kind, node);
		}
	}

	const desiredKinds: BehaviorCaseKind[] = [];
	if (nextState.nextReply) {
		desiredKinds.push('reply');
	}
	if (nextState.nextClick) {
		desiredKinds.push('click');
	}
	if (nextState.nextReply || nextState.nextClick) {
		desiredKinds.push('timeout');
	}

	const sourceNodeForLayout = withOptimisticBehaviorSourceHeight(
		sourceNode,
		!existingSwitchNode && desiredKinds.length > 0
	);
	const sourceStepName = getSourceStepName(step, nodes, sourceNodeId);

	if (desiredKinds.length === 0) {
		const removedNodeIds = new Set<string>();
		for (const caseNode of existingCaseNodes) {
			removedNodeIds.add(caseNode.id);
			for (const descendantId of getDescendantNodeIds(caseNode.id, edges)) {
				removedNodeIds.add(descendantId);
			}
		}
		removedNodeIds.add(switchNodeId);

		const nextNodesWithoutSwitch = nodes.filter(node => !removedNodeIds.has(node.id));
		const nextEdgesWithoutSwitch = edges.filter(
			edge =>
				!removedNodeIds.has(edge.source) &&
				!removedNodeIds.has(edge.target) &&
				(edge.data as { switchNodeId?: string } | undefined)?.switchNodeId !== switchNodeId
		);
		const nextLaidOutNodes = applySwitchBranchLayout(nextNodesWithoutSwitch, nextEdgesWithoutSwitch);
		return {
			nodes: nextLaidOutNodes,
			edges: nextEdgesWithoutSwitch,
		};
	}

	const desiredCaseNodes: Node<BaseNodeData>[] = [];
	const desiredCases: BehaviorCaseLike[] = [];
	const keptCaseNodeIds = new Set<string>();
	const updatedParentCaseInfoByCaseId = new Map<string, Partial<ParentCaseInfo>>();

	for (let caseIndex = 0; caseIndex < desiredKinds.length; caseIndex++) {
		const kind = desiredKinds[caseIndex];
		const existingCase = existingCases.find(candidate => getBehaviorCaseKind(candidate) === kind);
		const existingCaseNode = existingCaseNodeByKind.get(kind);
		const caseId =
			existingCase?.id ||
			(existingCaseNode?.data as BehaviorCaseNodeData | undefined)?.caseId ||
			`temp-${switchNodeId}-${kind}`;
		const baseCase: BehaviorCaseLike =
			kind === 'reply'
				? { ...createReplyCasePayload(step), id: caseId }
				: kind === 'click'
					? { ...createClickCasePayload(), id: caseId }
					: {
							...createTimeoutCasePayload(DEFAULT_TIMEOUT_CASE_LABEL),
							id: caseId,
							isDefault: true,
							isTimeout: true,
						};
		const mergedCase: BehaviorCaseLike = {
			...existingCase,
			...baseCase,
			id: caseId,
			name: kind === 'timeout' ? DEFAULT_TIMEOUT_CASE_LABEL : baseCase.name,
			isDefault: kind === 'timeout',
			isTimeout: kind === 'timeout',
		};
		desiredCases.push(mergedCase);

		const createdNode = createBehaviorCaseNode(
			{
				...mergedCase,
				engagementType:
					typeof mergedCase.engagementType === 'number' ? engagementTypeOf(mergedCase) : mergedCase.engagementType,
				waitTimeoutDays: nextState.nextTimeoutDays,
				sourceStepName,
			},
			caseIndex,
			switchNodeId,
			existingCaseNode?.position || sourceNode.position,
			(existingCaseNode?.data as BehaviorCaseNodeData | undefined)?.childTemplateId
		);
		const preservedSegmentPathKey = (existingCaseNode?.data as BehaviorCaseNodeData | undefined)?.segmentPathKey;
		const nextSegmentPathKey =
			preservedSegmentPathKey ||
			buildLocalBehaviorSegmentPathKey({
				templateId: template?.id,
				parentCaseInfo,
				switchNodeId,
				caseId,
				caseIndex,
			});
		const nextCaseNode: Node<BaseNodeData> = {
			...createdNode,
			id: existingCaseNode?.id || createdNode.id,
			position: existingCaseNode?.position || createdNode.position,
			data: {
				...createdNode.data,
				childTemplateId: (existingCaseNode?.data as BehaviorCaseNodeData | undefined)?.childTemplateId,
				segmentPathKey: nextSegmentPathKey,
			},
		};
		desiredCaseNodes.push(nextCaseNode);
		keptCaseNodeIds.add(nextCaseNode.id);
		updatedParentCaseInfoByCaseId.set(caseId, {
			caseIndex,
			caseName:
				(nextCaseNode.data as BehaviorCaseNodeData).caseName || (nextCaseNode.data as BehaviorCaseNodeData).label,
			isDefault: (nextCaseNode.data as BehaviorCaseNodeData).isDefault,
			caseColor: (nextCaseNode.data as BehaviorCaseNodeData).caseColor,
			parentSwitchNodeId: switchNodeId,
			caseId,
			segmentPathKey: nextSegmentPathKey,
			isBehaviorCase: true,
			sourceStepName,
		});
	}

	const idsToRemove = new Set<string>();
	for (const caseNode of existingCaseNodes) {
		if (keptCaseNodeIds.has(caseNode.id)) {
			continue;
		}
		idsToRemove.add(caseNode.id);
		for (const descendantId of getDescendantNodeIds(caseNode.id, edges)) {
			idsToRemove.add(descendantId);
		}
	}

	const updatedNodes = nodes
		.filter(node => {
			if (idsToRemove.has(node.id)) {
				return false;
			}
			if (node.id === switchNodeId) {
				return false;
			}
			if (existingCaseNodes.some(caseNode => caseNode.id === node.id)) {
				return false;
			}
			return true;
		})
		.map(node => {
			if (node.id === sourceNodeId) {
				return sourceNodeForLayout;
			}

			const parentInfo = (node.data as { parentCaseInfo?: ParentCaseInfo }).parentCaseInfo;
			if (
				!parentInfo ||
				!parentInfo.isBehaviorCase ||
				parentInfo.parentSwitchNodeId !== switchNodeId ||
				!parentInfo.caseId
			) {
				return node;
			}

			const nextParentInfo = updatedParentCaseInfoByCaseId.get(parentInfo.caseId);
			if (!nextParentInfo) {
				return node;
			}

			return {
				...node,
				data: {
					...node.data,
					parentCaseInfo: {
						...parentInfo,
						...nextParentInfo,
					},
				},
			};
		});

	const updatedSwitchNode =
		existingSwitchNode && existingSwitchStep
			? ({
					...existingSwitchNode,
					hidden: true,
					data: {
						...existingSwitchNode.data,
						summary: `${desiredCases.length} behavior paths`,
						automationStep: {
							...existingSwitchStep,
							sourceStepId: existingSwitchStep.sourceStepId || sourceStepId,
							waitTimeout: `${nextState.nextTimeoutDays}.00:00:00`,
							cases: desiredCases,
						},
						automationTemplate: template,
					},
				} satisfies Node<BaseNodeData>)
			: createOptimisticBehaviorSwitchNode({
					switchNodeId,
					sourceNode: sourceNodeForLayout,
					template,
					sourceStepId,
					waitTimeout: `${nextState.nextTimeoutDays}.00:00:00`,
					cases: desiredCases,
				});

	const updatedEdges = edges.filter(edge => {
		if (idsToRemove.has(edge.source) || idsToRemove.has(edge.target)) {
			return false;
		}
		return (edge.data as { switchNodeId?: string } | undefined)?.switchNodeId !== switchNodeId;
	});

	for (let caseIndex = 0; caseIndex < desiredCaseNodes.length; caseIndex++) {
		const caseNode = desiredCaseNodes[caseIndex];
		const caseData = caseNode.data as BehaviorCaseNodeData;
		updatedEdges.push({
			id: `${sourceNodeId}-to-${caseNode.id}`,
			source: sourceNodeId,
			target: caseNode.id,
			sourceHandle: 'default',
			type: EDGE_TYPES.BRANCH,
			data: {
				caseId: caseData.caseId,
				caseIndex,
				totalCases: desiredCaseNodes.length,
				isDefault: caseData.isDefault,
				caseName: caseData.caseName || caseData.label,
				caseColor: caseData.caseColor,
				switchNodeId,
			},
		});
	}

	const nextNodes = [...updatedNodes, updatedSwitchNode, ...desiredCaseNodes];
	const laidOutNodes = applySwitchBranchLayout(nextNodes, updatedEdges);
	return {
		nodes: laidOutNodes,
		edges: updatedEdges,
	};
}

function getBehaviorCaseColor(
	caseNode: Node<BaseNodeData> | undefined,
	fallbackColor: string | undefined
): string | undefined {
	const caseColor = (caseNode?.data as BehaviorCaseNodeData | undefined)?.caseColor;
	return caseColor || fallbackColor;
}

export function buildPersistedBehaviorCaseAssignments({
	nodes,
	optimisticSwitchNodeId,
	persistedSwitchStep,
}: {
	nodes: Node<BaseNodeData>[];
	optimisticSwitchNodeId: string;
	persistedSwitchStep: BehaviorSwitchStepPayload;
}): Map<string, PersistedBehaviorCaseAssignment> {
	const existingCaseNodes = nodes.filter(node => {
		if (node.type !== NODE_TYPES.BEHAVIOR_CASE) {
			return false;
		}
		return (node.data as BehaviorCaseNodeData).parentSwitchNodeId === optimisticSwitchNodeId;
	});
	const existingCaseNodeByKind = new Map<BehaviorCaseKind, Node<BaseNodeData>>();
	for (const node of existingCaseNodes) {
		const kind = getBehaviorCaseNodeKind(node.data as BehaviorCaseNodeData);
		if (kind) {
			existingCaseNodeByKind.set(kind, node);
		}
	}

	const assignments = new Map<string, PersistedBehaviorCaseAssignment>();
	for (const caseStatement of persistedSwitchStep.cases || []) {
		const behaviorCase = caseStatement as BehaviorCaseLike;
		const kind = getBehaviorCaseKind(behaviorCase);
		if (!kind) {
			continue;
		}
		const existingCaseNode = existingCaseNodeByKind.get(kind);
		const assignment: PersistedBehaviorCaseAssignment = {
			kind,
			caseId: behaviorCase.id,
			caseName: behaviorCase.name,
			caseType: (behaviorCase._type || behaviorCase.caseType || '') as string,
			engagementType:
				typeof behaviorCase.engagementType === 'number' ? engagementTypeOf(behaviorCase) : behaviorCase.engagementType,
			childTemplateId: behaviorCase.automationTemplateId,
			isDefault: isTimeoutBehaviorCase(behaviorCase),
			isTimeout: isTimeoutBehaviorCase(behaviorCase),
			caseColor: getBehaviorCaseColor(existingCaseNode, undefined),
		};

		if (existingCaseNode) {
			assignments.set(existingCaseNode.id, assignment);
		}
		if (behaviorCase.id) {
			assignments.set(behaviorCase.id, assignment);
		}
		assignments.set(kind, assignment);
	}

	return assignments;
}

export function syncOptimisticBehaviorNodes({
	nodes,
	optimisticSwitchNodeId,
	persistedSwitchStep,
	automationTemplate,
	caseAssignments,
}: {
	nodes: Node<BaseNodeData>[];
	optimisticSwitchNodeId: string;
	persistedSwitchStep: BehaviorSwitchStepPayload;
	automationTemplate?: Api.IAutomationTemplate;
	caseAssignments: Map<string, PersistedBehaviorCaseAssignment>;
}): Node<BaseNodeData>[] {
	return nodes.map(node => {
		if (node.id === optimisticSwitchNodeId) {
			const existingStep = (node.data as { automationStep?: BehaviorSwitchLike }).automationStep;
			return {
				...node,
				data: {
					...node.data,
					stepId: persistedSwitchStep.id,
					automationStep: {
						...existingStep,
						...persistedSwitchStep,
					},
					automationTemplate,
					summary: `${persistedSwitchStep.cases?.length ?? 0} behavior paths`,
					stepData: {
						...(node.data as { stepData?: Record<string, unknown> }).stepData,
						name: 'Conditional Behavior',
					},
				},
			};
		}

		if (node.type === NODE_TYPES.BEHAVIOR_CASE) {
			const caseData = node.data as BehaviorCaseNodeData;
			if (caseData.parentSwitchNodeId !== optimisticSwitchNodeId) {
				return node;
			}

			const caseKind = getBehaviorCaseNodeKind(caseData);
			const caseAssignment =
				caseAssignments.get(node.id) ||
				(caseData.caseId ? caseAssignments.get(caseData.caseId) : undefined) ||
				(caseKind ? caseAssignments.get(caseKind) : undefined);
			if (!caseAssignment) {
				return node;
			}

			return {
				...node,
				data: {
					...caseData,
					caseId: caseAssignment.caseId || caseData.caseId,
					caseName: caseAssignment.caseName || caseData.caseName || caseData.label,
					label: caseAssignment.caseName || caseData.label,
					caseType: caseAssignment.caseType || caseData.caseType,
					engagementType: caseAssignment.engagementType || caseData.engagementType,
					childTemplateId: caseAssignment.childTemplateId || caseData.childTemplateId,
					isDefault: caseAssignment.isDefault,
					isTimeout: caseAssignment.isTimeout,
					caseColor: caseAssignment.caseColor || caseData.caseColor,
				},
			};
		}

		const parentCaseInfo = (node.data as { parentCaseInfo?: ParentCaseInfo }).parentCaseInfo;
		if (
			!parentCaseInfo ||
			!parentCaseInfo.isBehaviorCase ||
			parentCaseInfo.parentSwitchNodeId !== optimisticSwitchNodeId
		) {
			return node;
		}

		const parentCaseKind = getBehaviorParentCaseKind(parentCaseInfo);
		const parentCaseAssignment =
			(parentCaseInfo.caseId ? caseAssignments.get(parentCaseInfo.caseId) : undefined) ||
			(parentCaseKind ? caseAssignments.get(parentCaseKind) : undefined);
		if (!parentCaseAssignment) {
			return node;
		}

		return {
			...node,
			data: {
				...node.data,
				parentCaseInfo: {
					...parentCaseInfo,
					caseId: parentCaseAssignment.caseId || parentCaseInfo.caseId,
					caseName: parentCaseAssignment.caseName || parentCaseInfo.caseName,
					isDefault: parentCaseAssignment.isDefault,
					caseColor: parentCaseAssignment.caseColor || parentCaseInfo.caseColor,
				},
			},
		};
	});
}

export function syncOptimisticBehaviorEdges({
	edges,
	optimisticSwitchNodeId,
	caseAssignments,
}: {
	edges: Edge[];
	optimisticSwitchNodeId: string;
	caseAssignments: Map<string, PersistedBehaviorCaseAssignment>;
}): Edge[] {
	return edges.map(edge => {
		if (
			edge.type !== EDGE_TYPES.BRANCH ||
			(edge.data as { switchNodeId?: string } | undefined)?.switchNodeId !== optimisticSwitchNodeId
		) {
			return edge;
		}

		const edgeCaseId = (edge.data as { caseId?: string } | undefined)?.caseId;
		const edgeCaseName = (edge.data as { caseName?: string } | undefined)?.caseName;
		const edgeCaseKind = getBehaviorCaseKind({
			id: edgeCaseId,
			name: edgeCaseName,
			isDefault: (edge.data as { isDefault?: boolean } | undefined)?.isDefault,
			isTimeout: (edge.data as { isDefault?: boolean } | undefined)?.isDefault,
		});
		const edgeAssignment =
			(edge.target ? caseAssignments.get(edge.target) : undefined) ||
			(edgeCaseId ? caseAssignments.get(edgeCaseId) : undefined) ||
			(edgeCaseKind ? caseAssignments.get(edgeCaseKind) : undefined);
		if (!edgeAssignment) {
			return edge;
		}

		return {
			...edge,
			data: {
				...edge.data,
				caseId: edgeAssignment.caseId,
				caseName: edgeAssignment.caseName || (edge.data as { caseName?: string } | undefined)?.caseName,
				isDefault: edgeAssignment.isDefault,
				caseColor: edgeAssignment.caseColor || (edge.data as { caseColor?: string } | undefined)?.caseColor,
			},
		};
	});
}
